import { useLocation } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { IN_PAGE_SEARCH_PARAM_KEY } from '@modules/professional/config/constants';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';
import {
    useBookMeetingGateEnabled,
    useOpenBookMeetingGate,
} from '~/modules-rocco/booking/book-meeting-gate-state';
import { DetailCreditsRow } from '~/ui-rocco/pages/detail-shared';
import { buildProfessionalTrustSignals } from '../utils/professional-trust-signals';
import { BUSINESS_FOCUS_TEXT } from './ProfessionalSpecList';

/** The row's 12px uppercase names, as a control. */
const ACTION_CLASS =
    'cursor-pointer bg-transparent p-0 text-left font-sans text-[0.75rem] leading-normal text-[#181818] uppercase no-underline transition-opacity hover:no-underline hover:opacity-70';

const BOOK_A_MEETING = 'Book a Meeting';

const HOURS_PER_DAY = 24;
/**
 * Past two days a response time reads as a warning rather than a reason to get
 * in touch, so it is left off, as the practice card on project pages leaves it.
 */
const MAX_SHOWN_RESPONSE_HOURS = 48;

/**
 * "Within 3 hours", "Within 2 days" — or nothing when the practice has too few
 * enquiries to measure (the field is 0) or answers slower than two days.
 */
const responseTimeText = (hours: number | null | undefined) => {
    if (!hours || hours <= 0 || hours > MAX_SHOWN_RESPONSE_HOURS) {
        return undefined;
    }
    if (hours < HOURS_PER_DAY) {
        return `Within ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const days = Math.round(hours / HOURS_PER_DAY);
    return `Within ${days} ${days === 1 ? 'day' : 'days'}`;
};
const ENQUIRE_NOW = 'Enquire Now';

/**
 * The phone the practice publishes, if any — the tracking number where one is
 * set, as the reveal shows it.
 */
const publishedPhone = (professional: {
    PhoneTracking?: string | null;
    Phone?: string | null;
}) => professional.PhoneTracking || professional.Phone || undefined;

/** Locations the practice shows on its Contact tab. */
const shownLocationCount = (professional: {
    LibraryLocations?: readonly { LocationHidden?: boolean | null }[] | null;
}) =>
    (professional.LibraryLocations ?? []).filter(
        (location) => !location.LocationHidden,
    ).length;

/**
 * The ways to reach the practice, as a column of the meta row: a meeting and an
 * enquiry through the same flows as the page's CTAs, the phone number behind
 * the same reveal as the spec list, and the practice's locations on the Contact
 * tab. The enquiry channels respect the practice's enquiry audience.
 */
const GetInTouchLinks = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    const { showEnquiryCTA } = useEnquiryVisibility(initial);
    const { openEnquiryModal } = useEnquiryModal(
        {
            professional: initial,
            itemType: 'professional',
            siteTreeID: initial.ID,
            itemTitle: initial.Title,
            includeBranch: true,
        },
        // The header CTAs already own the `enquire` URL param.
        { autoOpenFromUrl: false },
    );
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();
    const { revealed, onRevealClick } = useClickToReveal({
        revealType: 'Phone',
        revealKey: `professional-${initial.ID}`,
        professionalId: initial.ID,
    });

    const phone = publishedPhone(initial);
    const locationCount = shownLocationCount(initial);

    // With the booking gate off, a meeting request goes through the enquiry
    // modal, as every other Book a Meeting entry point does.
    const bookMeeting = () =>
        bookMeetingEnabled
            ? openBookMeetingGate({
                  professionalUrlSegment: initial.URLSegment,
                  siteTreeId: initial.ID,
                  professionalName: initial.Title,
              })
            : openEnquiryModal({
                  title: `${BOOK_A_MEETING} with ${initial.Title}`,
                  ctaLabel: BOOK_A_MEETING,
              });

    return (
        <ul className="m-0 flex list-none flex-col gap-0 p-0">
            {showEnquiryCTA ? (
                <>
                    <li>
                        <button
                            type="button"
                            onClick={bookMeeting}
                            className={ACTION_CLASS}
                        >
                            {BOOK_A_MEETING}
                        </button>
                    </li>
                    <li>
                        <button
                            type="button"
                            onClick={() =>
                                openEnquiryModal({
                                    title: ENQUIRE_NOW,
                                    ctaLabel: ENQUIRE_NOW,
                                })
                            }
                            className={ACTION_CLASS}
                        >
                            {ENQUIRE_NOW}
                        </button>
                    </li>
                </>
            ) : null}
            {phone ? (
                <li>
                    {revealed ? (
                        <a
                            href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                            className={ACTION_CLASS}
                        >
                            {phone}
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={onRevealClick}
                            className={ACTION_CLASS}
                        >
                            View Phone number
                        </button>
                    )}
                </li>
            ) : null}
            {locationCount > 0 ? (
                <li>
                    <RoccoLink
                        to={`${professionalTabBase(initial.URLSegment, pathname)}/contact`}
                        className={ACTION_CLASS}
                    >
                        {locationCount === 1
                            ? 'View location'
                            : 'View locations'}
                    </RoccoLink>
                </li>
            ) : null}
        </ul>
    );
};

/**
 * A tag searches the tab that holds the work it names: a supplier's products, or
 * every other practice's projects.
 */
const tagLink = (tabBase: string, isSupplier: boolean, tag: string) => {
    const tab = isSupplier ? 'our-products' : 'case-studies';
    const query = new URLSearchParams({ [IN_PAGE_SEARCH_PARAM_KEY]: tag });
    return `${tabBase}/${tab}?${query.toString()}`;
};

/**
 * The product page's meta row, on the profile: the practice's services as tags
 * that search its projects or products, then how to get in touch, its business
 * details and the bodies it belongs to. The tags, the details and the
 * associations each hide when empty.
 */
export const ProfessionalMetaRow = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    // Read here as well as inside the column: whether there is any way to get
    // in touch decides whether the column exists at all, and a label over
    // nothing is what the row is built to avoid.
    const { showEnquiryCTA } = useEnquiryVisibility({
        EnquiryAudience: initial?.EnquiryAudience,
    });
    if (!initial) return null;

    const tabBase = professionalTabBase(initial.URLSegment, pathname);
    const services = Array.from(
        new Set(
            (initial.Services ?? [])
                .map((service) => service.trim())
                .filter((service) => service !== ''),
        ),
    ).map((label) => ({
        label,
        to: tagLink(tabBase, Boolean(initial.IsManufacturer), label),
    }));

    // Deduped on name, keeping the first segment seen: an association is a
    // professional in its own right, so its name points at its profile.
    const associations = Array.from(
        (initial.Associations ?? [])
            .reduce((byName, association) => {
                const name = association.Title?.trim();
                if (!name || byName.has(name)) return byName;
                byName.set(name, {
                    name,
                    link: association.URLSegment
                        ? `/professional/${association.URLSegment}`
                        : undefined,
                });
                return byName;
            }, new Map<string, { name: string; link?: string }>())
            .values(),
    );

    // Only what the profile actually carries: each row drops out without a
    // value, and the column with them.
    const { badges } = buildProfessionalTrustSignals(initial);
    const memberSince = badges
        .find((badge) => badge.id === 'verified')
        ?.detail?.replace(/^Since\s+/, '');
    const awardCount = initial.Awards?.edges?.length ?? 0;
    const businessFocus = initial.BusinessFocus
        ? BUSINESS_FOCUS_TEXT[initial.BusinessFocus]
        : undefined;
    const businessDetails = [
        {
            label: 'Category:',
            value: initial.PrimaryCategory?.PrettyTitle?.trim() || undefined,
        },
        {
            label: 'Average response time:',
            value: responseTimeText(initial.EnquiryAvgResponseHours),
        },
        { label: 'Business focus:', value: businessFocus },
        { label: 'Verified ArchiPro member since:', value: memberSince },
        {
            label: 'Award winning professional:',
            value:
                awardCount > 0
                    ? `${awardCount} ${awardCount === 1 ? 'award' : 'awards'}`
                    : undefined,
        },
    ].filter((pair): pair is { label: string; value: string } =>
        Boolean(pair.value),
    );

    // Nothing to offer means no column: with enquiries closed, no published
    // phone and every location hidden, the list comes out empty and the label
    // would stand over blank space.
    const hasGetInTouch =
        showEnquiryCTA ||
        !!publishedPhone(initial) ||
        shownLocationCount(initial) > 0;

    return (
        <DetailCreditsRow
            tags={services}
            columns={[
                {
                    label: 'Get in touch',
                    entries: [],
                    node: hasGetInTouch ? <GetInTouchLinks /> : undefined,
                },
                {
                    label: 'Business details',
                    entries: [],
                    pairs: businessDetails,
                },
                { label: 'Associations', entries: associations },
            ]}
        />
    );
};
